import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  getProductsList,
  updateProductEanNumber,
} from "../../../../data/epos";

const compactCellSx = {
  fontSize: "0.75rem",
  py: 0.35,
  px: 0.75,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const inputSx = {
  "& .MuiInputBase-root": { height: 28, fontSize: "0.78rem", borderRadius: 1 },
  "& .MuiInputBase-input": { py: 0.25, px: 0.75 },
  "& .MuiOutlinedInput-notchedOutline": {
    borderWidth: "1px !important",
    borderColor: "#000 !important",
  },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
    borderWidth: "1px !important",
    borderColor: "#000 !important",
  },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderWidth: "1px !important",
    borderColor: "#000 !important",
  },
};

const readOnlyInputSx = {
  ...inputSx,
  "& .MuiInputBase-input": {
    py: 0.25,
    px: 0.75,
    color: "#000",
    WebkitTextFillColor: "#000",
  },
  "& .Mui-disabled": {
    color: "#000",
    WebkitTextFillColor: "#000",
  },
};

const headerCellSx = {
  ...compactCellSx,
  fontWeight: 600,
  bgcolor: "#f5f5f5",
  color: "#000",
};

const STOCK_TABLE_COLUMNS = [
  { key: "code", label: "Code", align: "left", width: "8%" },
  { key: "name", label: "Name", align: "left", width: "30%" },
  { key: "barcode", label: "Barcode", align: "left", width: "14%" },
  { key: "offer", label: "Offer CC", align: "left", width: "9%" },
  { key: "expiry", label: "Expiry CC", align: "left", width: "10%" },
  { key: "price1", label: "Price 1", align: "right", width: "9%" },
  { key: "price5", label: "Price 5", align: "right", width: "9%" },
  { key: "vat", label: "VAT", align: "left", width: "11%" },
];

const btnSx = {
  height: 28,
  minHeight: 28,
  fontSize: "0.78rem",
  whiteSpace: "nowrap",
  px: 1.25,
};

const formatPriceDisplay = (value) => {
  if (value === undefined || value === null || value === "") {
    return "";
  }
  const num = parseFloat(value);
  if (Number.isFinite(num)) {
    return num.toFixed(2);
  }
  return String(value);
};

const resolveBandPrice = (product, band) => {
  const key = `price_${band}`;
  const camelKey = `price${band}`;
  const direct = product?.[key] ?? product?.[camelKey];
  if (direct !== undefined && direct !== null && direct !== "") {
    return formatPriceDisplay(direct);
  }

  const prices = product?.prices ?? product?.band_prices ?? product?.product_prices;
  if (Array.isArray(prices)) {
    const entry = prices.find(
      (row) => String(row?.band_id ?? row?.band ?? row?.price_brand ?? "") === String(band)
    );
    if (entry) {
      return formatPriceDisplay(entry.price ?? entry.selling_price ?? entry.amount);
    }
    const byIndex = prices[band - 1];
    if (byIndex != null) {
      return formatPriceDisplay(byIndex.price ?? byIndex.selling_price ?? byIndex);
    }
  }
  if (prices && typeof prices === "object" && !Array.isArray(prices)) {
    const nested = prices[key] ?? prices[camelKey] ?? prices[String(band)];
    if (nested !== undefined && nested !== null && nested !== "") {
      return formatPriceDisplay(nested);
    }
  }

  const activeBand = parseInt(product?.band_id ?? product?.price_brand ?? 0, 10);
  if (activeBand === band) {
    const fallback =
      product?.final_price ??
      product?.final_split_price ??
      product?.price ??
      product?.split_price;
    if (fallback !== undefined && fallback !== null && fallback !== "") {
      return formatPriceDisplay(fallback);
    }
  }

  return "";
};

const resolveOfferCc = (product) =>
  product?.offer_cc ??
  product?.offer_price_cc ??
  product?.cc_offer_price ??
  product?.offer_price ??
  "";

const resolveExpiryCc = (product) =>
  product?.expiry_cc ??
  product?.expiry_date_cc ??
  product?.offer_expiry_date ??
  product?.expiry_date ??
  "";

const resolveProductLevel = (product) => {
  const piece =
    parseInt(product?.availablePieceQty ?? product?.available_piece_qty ?? 0, 10) || 0;
  const box =
    parseInt(product?.availableBoxQty ?? product?.available_box_qty ?? 0, 10) || 0;
  if (piece + box > 0) {
    return String(piece + box);
  }
  const stock = product?.stock ?? product?.level;
  return stock != null && stock !== "" ? String(stock) : "";
};

const buildFormFromProduct = (product) => ({
  productId: product?.id ?? "",
  productCode: product?.product_code ?? "",
  productName: product?.product_name ?? "",
  innerEanNumber: product?.inner_ean_number ?? "",
  outerEanNumber: product?.outer_ean_number ?? "",
  level: resolveProductLevel(product),
  taxName: product?.tax_name ?? "",
  price1: resolveBandPrice(product, 1),
  price2: resolveBandPrice(product, 2),
  price3: resolveBandPrice(product, 3),
  price4: resolveBandPrice(product, 4),
  price5: resolveBandPrice(product, 5),
  offerCc: resolveOfferCc(product),
  expiryCc: resolveExpiryCc(product),
});

const DetailRow = ({ label, children }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
    <Typography variant="caption" sx={{ width: 52, flexShrink: 0, fontSize: "0.72rem" }}>
      {label}
    </Typography>
    <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
  </Box>
);

const RhpStockDialog = ({
  open,
  onClose,
  customerId,
  onNotify,
}) => {
  const [searchInput, setSearchInput] = useState("");
  const [products, setProducts] = useState([]);
  const [selectedRowIndex, setSelectedRowIndex] = useState(null);
  const [form, setForm] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const tableContainerRef = useRef(null);

  const resetState = useCallback(() => {
    setSearchInput("");
    setProducts([]);
    setSelectedRowIndex(null);
    setForm(null);
    setIsSearching(false);
    setIsSaving(false);
  }, []);

  const selectProductAtIndex = useCallback((index, listOverride) => {
    const source = listOverride ?? products;
    const product = source[index];
    if (!product) {
      return;
    }
    setSelectedRowIndex(index);
    setForm(buildFormFromProduct(product));
    requestAnimationFrame(() => {
      const rowEl = tableContainerRef.current?.querySelector(
        `tbody tr[data-row-index="${index}"]`
      );
      rowEl?.scrollIntoView({ block: "nearest", inline: "nearest" });
    });
  }, [products]);

  const moveSelectionByStep = useCallback(
    (step) => {
      if (products.length === 0) {
        return;
      }
      const baseIndex =
        selectedRowIndex === null ? (step > 0 ? -1 : 0) : selectedRowIndex;
      const nextIndex = Math.max(0, Math.min(baseIndex + step, products.length - 1));
      selectProductAtIndex(nextIndex);
    },
    [products.length, selectedRowIndex, selectProductAtIndex]
  );

  useEffect(() => {
    if (!open) {
      resetState();
    }
  }, [open, resetState]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handleListKeyDown = (event) => {
      if (event.key !== "ArrowDown" && event.key !== "ArrowUp") {
        return;
      }
      if (products.length === 0) {
        return;
      }

      const target = event.target;
      const isBarcodeField = target?.closest?.("[data-stock-barcode-input]");
      if (isBarcodeField) {
        return;
      }

      event.preventDefault();
      moveSelectionByStep(event.key === "ArrowDown" ? 1 : -1);
    };

    window.addEventListener("keydown", handleListKeyDown);
    return () => window.removeEventListener("keydown", handleListKeyDown);
  }, [open, products.length, moveSelectionByStep]);

  const handleSearch = async () => {
    const pattern = searchInput.trim();
    if (!pattern) {
      onNotify?.("Enter product code or barcode", "warning");
      return;
    }
    if (!customerId) {
      onNotify?.("Please select a customer first", "warning");
      return;
    }

    setIsSearching(true);
    try {
      const response = await getProductsList({
        pattern,
        customer_id: customerId,
        platform: "4",
      });
      const list = response?.product_details || response?.product_detials || [];
      setProducts(list);
      if (list.length === 0) {
        setSelectedRowIndex(null);
        setForm(null);
        onNotify?.("No products found", "warning");
        return;
      }
      selectProductAtIndex(0, list);
    } catch {
      onNotify?.("Unable to fetch products", "error");
    } finally {
      setIsSearching(false);
    }
  };

  const handleSelectProduct = (product, index) => {
    selectProductAtIndex(index);
  };

  const handleSearchInputKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSearch();
      return;
    }
    if (
      (event.key === "ArrowDown" || event.key === "ArrowUp") &&
      products.length > 0
    ) {
      event.preventDefault();
      moveSelectionByStep(event.key === "ArrowDown" ? 1 : -1);
    }
  };

  const handleTableKeyDown = (event) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      moveSelectionByStep(1);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      moveSelectionByStep(-1);
    }
  };

  const updateFormField = (field, value) => {
    setForm((prev) => (prev ? { ...prev, [field]: value } : prev));
  };

  const handleSave = async () => {
    if (!form?.productId) {
      onNotify?.("Select a product first", "warning");
      return;
    }

    setIsSaving(true);
    try {
      const eanResponse = await updateProductEanNumber({
        productId: form.productId,
        innerEanNumber: form.innerEanNumber,
        outerEanNumber: form.outerEanNumber,
      });
      if (eanResponse?.success === false) {
        throw new Error(eanResponse?.message || "Failed to update barcode");
      }

      setProducts((prev) =>
        prev.map((item) =>
          String(item.id) === String(form.productId)
            ? {
                ...item,
                inner_ean_number: form.innerEanNumber,
                outer_ean_number: form.outerEanNumber,
              }
            : item
        )
      );
      onNotify?.(eanResponse?.message || "Barcode updated", "success");
    } catch (error) {
      onNotify?.(error?.message || "Failed to save product", "error");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{ sx: { width: 820, maxWidth: "96vw", maxHeight: "92vh" } }}
    >
      <DialogContent sx={{ pt: 1.5, px: 2, pb: 2, overflow: "auto" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1.25, flexWrap: "nowrap" }}>
          <Typography sx={{ fontSize: "0.95rem", fontWeight: 600, color: "#000", flexShrink: 0, mr: 0.5 }}>
            Stock
          </Typography>
          <Typography variant="caption" sx={{ fontSize: "0.72rem", flexShrink: 0, color: "#000" }}>
            Product Code
          </Typography>
          <TextField
            size="small"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={handleSearchInputKeyDown}
            placeholder="Code / Barcode"
            data-stock-search-input
            sx={{ width: 160, ...inputSx }}
            autoComplete="off"
          />
          <Button
            variant="outlined"
            size="small"
            className="button-variant-outlined"
            onClick={handleSearch}
            disabled={isSearching}
            sx={{ ...btnSx, minWidth: 68 }}
          >
            {isSearching ? "..." : "Display"}
          </Button>
        </Box>

        <TableContainer
          ref={tableContainerRef}
          component={Paper}
          variant="outlined"
          tabIndex={0}
          onKeyDown={handleTableKeyDown}
          sx={{
            mb: 1.25,
            maxHeight: 260,
            minHeight: 130,
            borderColor: "#000",
            outline: "none",
            overflow: "auto",
            "&:focus": { boxShadow: "inset 0 0 0 1px #5b9bd5" },
          }}
        >
          <Table size="small" stickyHeader sx={{ tableLayout: "fixed", width: "100%" }}>
            <TableHead>
              <TableRow>
                {STOCK_TABLE_COLUMNS.map((column) => (
                  <TableCell
                    key={column.key}
                    align={column.align}
                    sx={{ ...headerCellSx, width: column.width }}
                  >
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {products.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ ...compactCellSx, py: 1, color: "#000" }}>
                    Search to view products
                  </TableCell>
                </TableRow>
              ) : (
                products.map((product, index) => {
                  const isSelected = selectedRowIndex === index;
                  return (
                    <TableRow
                      key={product.id}
                      data-row-index={index}
                      hover
                      selected={isSelected}
                      tabIndex={isSelected ? 0 : -1}
                      onClick={() => handleSelectProduct(product, index)}
                      sx={{
                        cursor: "pointer",
                        "&.Mui-selected": { bgcolor: "#c5dff8 !important" },
                        "&.Mui-selected:hover": { bgcolor: "#c5dff8 !important" },
                      }}
                    >
                      <TableCell sx={{ ...compactCellSx, color: "#000" }} title={product.product_code}>
                        {product.product_code || "-"}
                      </TableCell>
                      <TableCell
                        sx={{ ...compactCellSx, color: "#000", minWidth: 220, maxWidth: 280 }}
                        title={product.product_name}
                      >
                        {product.product_name || "-"}
                      </TableCell>
                      <TableCell sx={{ ...compactCellSx, color: "#000" }} title={product.inner_ean_number}>
                        {product.inner_ean_number || "-"}
                      </TableCell>
                      <TableCell sx={{ ...compactCellSx, color: "#000" }}>
                        {resolveOfferCc(product) || "-"}
                      </TableCell>
                      <TableCell sx={{ ...compactCellSx, color: "#000" }}>
                        {resolveExpiryCc(product) || "-"}
                      </TableCell>
                      <TableCell align="right" sx={{ ...compactCellSx, color: "#000" }}>
                        {resolveBandPrice(product, 1) || "-"}
                      </TableCell>
                      <TableCell align="right" sx={{ ...compactCellSx, color: "#000" }}>
                        {resolveBandPrice(product, 5) || "-"}
                      </TableCell>
                      <TableCell sx={{ ...compactCellSx, color: "#000" }}>
                        {product.tax_name || "-"}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Typography variant="caption" sx={{ fontWeight: 600, display: "block", mb: 0.75, color: "#000" }}>
          Details
        </Typography>

        {form ? (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1.1fr 0.9fr 0.8fr",
              gap: 1.25,
              mb: 1.25,
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.6 }}>
              <DetailRow label="Code">
                <TextField size="small" value={form.productCode} disabled fullWidth sx={readOnlyInputSx} />
              </DetailRow>
              <DetailRow label="BarCode">
                <TextField
                  size="small"
                  value={form.innerEanNumber}
                  onChange={(e) => updateFormField("innerEanNumber", e.target.value)}
                  fullWidth
                  sx={inputSx}
                  autoComplete="off"
                  data-stock-barcode-input
                />
              </DetailRow>
              <DetailRow label="Name">
                <TextField
                  size="small"
                  value={form.productName}
                  disabled
                  fullWidth
                  sx={readOnlyInputSx}
                  inputProps={{ title: form.productName }}
                />
              </DetailRow>
              <DetailRow label="Level">
                <TextField size="small" value={form.level} disabled fullWidth sx={readOnlyInputSx} />
              </DetailRow>
              <DetailRow label="VAT">
                <TextField size="small" value={form.taxName} disabled fullWidth sx={readOnlyInputSx} />
              </DetailRow>
            </Box>

            <Box>
              <Typography variant="caption" fontWeight={600} sx={{ display: "block", mb: 0.5, fontSize: "0.72rem", color: "#000" }}>
                Selling Price
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                {[1, 2, 3, 4, 5].map((band) => {
                  const key = `price${band}`;
                  return (
                    <Box key={band} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <Typography variant="caption" sx={{ width: 78, fontSize: "0.7rem", flexShrink: 0 }}>
                        {`Price ${band}`}
                      </Typography>
                      <TextField
                        size="small"
                        value={form[key]}
                        disabled
                        sx={{ width: 72, ...readOnlyInputSx }}
                      />
                    </Box>
                  );
                })}
              </Box>
            </Box>

            <Box>
              <Typography variant="caption" fontWeight={600} sx={{ display: "block", mb: 0.5, fontSize: "0.72rem", color: "#000" }}>
                Offer Price
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.6 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography variant="caption" sx={{ width: 78, fontSize: "0.7rem", flexShrink: 0 }}>
                    Offer CC
                  </Typography>
                  <TextField
                    size="small"
                    value={form.offerCc}
                    disabled
                    sx={{ width: 72, ...readOnlyInputSx }}
                  />
                </Box>
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}>
                  <Typography variant="caption" sx={{ width: 78, fontSize: "0.7rem", flexShrink: 0, pt: 0.5 }}>
                    Expiry CC
                  </Typography>
                  <Box>
                    <TextField
                      size="small"
                      value={form.expiryCc}
                      disabled
                      placeholder="dd/mm/yyyy"
                      sx={{ width: 92, ...readOnlyInputSx }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontSize: "0.65rem" }}>
                      (dd/mm/yyyy)
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        ) : (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.25 }}>
            Select a product from the list.
          </Typography>
        )}

        <Box sx={{ display: "flex", justifyContent: "space-between", pt: 0.5 }}>
          <Button
            variant="contained"
            size="small"
            className="button-variant-contained"
            onClick={handleSave}
            disabled={!form || isSaving}
            sx={{ ...btnSx, minWidth: 72 }}
          >
            {isSaving ? "Saving..." : "Save"}
          </Button>
          <Button
            variant="outlined"
            size="small"
            className="button-variant-outlined"
            onClick={onClose}
            sx={{ ...btnSx, minWidth: 72 }}
          >
            Close
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default RhpStockDialog;
